"use client";

import React, { useState } from "react";
import Image from "next/image";
import s from "../styles";
import Consts from "@common/config/consts";
import { dayjs } from "@lib/client";

/**
 * 指定部品リスト表示コンポーネント
 * shitei_list.ctpをReactに移植したコンポーネント
 *
 * @param {Object} props - プロパティ
 * @param {Object} props.data - データオブジェクト（partsList, request, answer, SessionData, partskind, sbunrui, hoshoSelect等を含む）
 * @param {string} props.quote_number - 見積番号
 * @param {Object} props.photoDialogProps - 写真ダイアログのプロパティ（open関数を含む）
 * @param {Object} props.fileupDialogProps - ファイルアップロードダイアログのプロパティ（open関数を含む）
 * @param {Object} props.AqbData - 在庫確認データ [PAID] => [total, answered, ...]
 * @param {Function} props.onAddParts - 部品追加時のコールバック (mitsumoriNo, raid) => Promise<{paid, seqnum}>
 * @param {Function} props.onDeleteParts - 部品削除時のコールバック (mitsumoriNo, prid, raid, seqnum) => Promise<void>
 * @param {Function} props.onOpenGenshaDetail - 入庫済み車輌詳細を開く関数 (genshaId) => void
 * @param {Function} props.onOpenQaDialog - 質問ダイアログを開く関数 (mitsumoriNo, key) => void
 * @param {Function} props.onOpenQabDialog - 在庫確認ダイアログを開く関数 (mitsumoriNo, key) => void
 * @returns {JSX.Element} 指定部品リスト表示コンポーネント
 */
export default function ShiteiListArea({ data, quote_number, photoDialogProps, fileupDialogProps, AqbData = {}, onAddParts, onDeleteParts, onOpenGenshaDetail, onOpenQaDialog, onOpenQabDialog }) {
  /**
   * 数値をカンマ区切りでフォーマットする
   * @param {number|string} num - フォーマット対象の数値
   * @returns {string} カンマ区切りの数値文字列
   */
  const formatNumber = (num) => {
    if (!num && num !== 0) return "";
    return Number(num).toLocaleString();
  };

  /**
   * 日付を y/m/d 形式でフォーマットする
   * @param {string} dateString - フォーマット対象の日付文字列
   * @returns {string} フォーマット後の日付文字列（y/m/d形式）、無効な場合はnull
   */
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const d = dayjs(dateString);
    if (!d.isValid()) return null;
    return d.format("YY/MM/DD");
  };

  /**
   * 発注フラグから状態テキストを取得する
   * @param {number} hacchuFlg - 発注フラグ
   * @returns {Object} { jyotai: string, bgcolor: string } 状態テキストと背景色クラス
   */
  const getHacchuStatus = (hacchuFlg) => {
    if (!hacchuFlg && hacchuFlg !== 0) {
      return { jyotai: "", bgcolor: "" };
    }

    switch (hacchuFlg) {
      case Consts.HACCHU_FLG.HASOZUMI:
        return { jyotai: "発送済", bgcolor: "" };
      case Consts.HACCHU_FLG.KAKUTEI:
        return { jyotai: "注文確定", bgcolor: "" };
      case Consts.HACCHU_FLG.NEBIKI:
        return { jyotai: "値引", bgcolor: "" };
      case Consts.HACCHU_FLG.HENPIN:
        return { jyotai: "返品", bgcolor: "" };
      case Consts.HACCHU_FLG.ZAIKONASHI:
        return { jyotai: "在庫なし回答", bgcolor: "nonegr" };
      case Consts.HACCHU_FLG.KIGENGIRE:
        return { jyotai: "在庫回答時間切れ", bgcolor: "nonegr" };
      default:
        return { jyotai: "", bgcolor: "" };
    }
  };

  /**
   * 発送可能日の値を表示テキストに変換する
   * @param {number} hasso - 発送可能日の値
   * @returns {string} 表示テキスト
   */
  const getHassoStatusText = (hasso) => {
    if (!hasso && hasso !== 0) return "";
    const status = Consts.SELECTION.HASSO_STATUS.find((item) => item.key === hasso);
    return status ? status.value : "";
  };

  /**
   * 部品カテゴリ名を取得する
   * @param {number} pkid - 部品カテゴリID
   * @param {Array} partskind - 部品カテゴリマスタ配列
   * @returns {string} 部品カテゴリ名
   */
  const getCategoryName = (pkid, partskind) => {
    if (!pkid || !partskind) return "";
    const category = partskind.find((cat) => cat?.Partskind?.PartsKindID === pkid);
    return category?.Partskind?.PartsKindName || "";
  };

  /**
   * 在庫確認ボタンの画像名を取得する
   * @param {string} paid - 部品回答ID
   * @param {string} requestLimit - 依頼期限（未使用、PHPテンプレートとの互換性のため残す）
   * @returns {string} ボタン画像名（空文字列の場合は表示しない）
   */
  const getQabButtonName = (paid) => {
    if (!paid) {
      return "";
    }

    const aqbData = AqbData[paid];
    if (!aqbData) {
      return "";
    } else if (!aqbData[2]) {
      return "qa_btn4";
    } else if (aqbData[2] >= aqbData[1]) {
      return "qa_btn5";
    } else {
      return "qa_btn4";
    }
  };

  // 在庫なしフラグの状態管理
  // PHPテンプレート: $SessionData == null の判定を再現するため、data?.SessionDataを直接使用
  const sessionData = data?.SessionData;
  // PHPテンプレート: ($SessionData == null) ? 0 : (empty($SessionData['Answer'][0][0]['ZaikoNashi'])) ? 0 : $SessionData['Answer'][0][0]['ZaikoNashi']
  const checkZaiko = sessionData == null || sessionData === undefined ? 0 : sessionData?.Answer?.[0]?.[0]?.ZaikoNashi || 0;
  const [zaikoNashi, setZaikoNashi] = useState(checkZaiko === 1);

  // 一括見積備考の状態管理
  // PHPテンプレート: ($SessionData == null) ? (empty($partsList) ? '' : $partsList[0]['PartsAnswer']['Bikou']) : $SessionData['Answer'][0][0]['biko']
  const initialBiko = sessionData == null || sessionData === undefined ? data?.partsList?.[0]?.PartsAnswer?.Bikou || "" : sessionData?.Answer?.[0]?.[0]?.biko || "";
  const [biko, setBiko] = useState(initialBiko);

  // 価格・送料・発送可能日・見積有効期限の状態管理（入力モード時のみ）
  // PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Answer'][0][0]['Price'])) ? '' : $SessionData['Answer'][0][0]['Price']
  const initialPrice = sessionData == null || sessionData === undefined ? "" : sessionData?.Answer?.[0]?.[0]?.Price || "";
  const [price, setPrice] = useState(initialPrice);
  // PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Answer'][0][0]['DeliveryPrice'])) ? '' : $SessionData['Answer'][0][0]['DeliveryPrice']
  const initialDeliveryPrice = sessionData == null || sessionData === undefined ? "" : sessionData?.Answer?.[0]?.[0]?.DeliveryPrice || "";
  const [deliveryPrice, setDeliveryPrice] = useState(initialDeliveryPrice);
  // PHPテンプレート: $SessionData == null の場合、$partsList[0]['PartsAnswer']['Hasso']を使用。そうでない場合は$SessionData['Answer'][0][0]['Hasso']を使用
  const initialHasso = sessionData == null || sessionData === undefined ? data?.partsList?.[0]?.PartsAnswer?.Hasso || "" : sessionData?.Answer?.[0]?.[0]?.Hasso || "";
  const [hasso, setHasso] = useState(initialHasso);
  // PHPテンプレート: ($SessionData == null) ? (empty($partsList) ? '' : date('Y/m/d', strtotime($partsList[0]['PartsAnswer']['Sell_Limit']))) : empty($SessionData['Answer'][0][0]['Sell_Limit']) ? '' : $SessionData['Answer'][0][0]['Sell_Limit']
  const initialSellLimit = sessionData == null || sessionData === undefined ? formatDate(data?.partsList?.[0]?.PartsAnswer?.Sell_Limit) || "" : sessionData?.Answer?.[0]?.[0]?.Sell_Limit || "";
  const [sellLimit, setSellLimit] = useState(initialSellLimit);

  // 追加商品リストの状態管理
  // PHPテンプレート: empty($SessionData['Add']) の場合、ループが実行されない
  // PHPのempty()は、null、undefined、空文字列、0、false、空配列に対してtrueを返す
  // Reactでは空配列でもループは実行されるが、lengthが0なので何も表示されない
  const initialAddList = sessionData?.Add && Array.isArray(sessionData.Add) && sessionData.Add.length > 0 ? sessionData.Add : [];
  const [addList, setAddList] = useState(initialAddList);
  const [rowCount, setRowCount] = useState(initialAddList.length);

  // 依頼期限の判定
  // PHPテンプレート: strtotime($request['OrderedRequest']['Request_Limit']) >= strtotime(Consts::CARRY_TAXIN_DATE)
  const isAfterCarryTaxinDate =
    data?.request?.OrderedRequest?.Request_Limit &&
    (dayjs(data.request.OrderedRequest.Request_Limit).isAfter(dayjs(Consts.CARRY_TAXIN_DATE)) || dayjs(data.request.OrderedRequest.Request_Limit).isSame(dayjs(Consts.CARRY_TAXIN_DATE), "day"));

  // 現車と紐付いている場合
  const isGensha = data?.answer?.[0]?.RequestAnswer?.GenshaID ? true : false;

  // 部品取り一括見積の表示フラグと状態を判定
  const partsList = data?.partsList || [];
  const firstParts = partsList[0];
  let viewFlg = 1; // ラベル表示
  let jyotai = "";
  let bgcolor = "";
  let btNameQab = "";

  if (!firstParts) {
    viewFlg = 0; // 入力
  } else {
    // PHPテンプレート: ($SessionData == null)? $partsList[0]['PartsAnswer']['id'] : $SessionData['Answer'][0][0]['PAID']
    // PHPでは$SessionData['Answer'][0][0]['PAID']が空の場合は空文字列が返されるが、フォールバックはない
    const paid = sessionData == null || sessionData === undefined ? firstParts?.PartsAnswer?.id : sessionData?.Answer?.[0]?.[0]?.PAID || undefined;
    btNameQab = getQabButtonName(paid);

    const hacchuFlg = firstParts?.PartsAnswer?.Hacchu_flg;
    const status = getHacchuStatus(hacchuFlg);
    jyotai = status.jyotai;
    bgcolor = status.bgcolor;

    if (!jyotai) {
      viewFlg = 0; // 入力
    }
  }

  // 在庫なしチェックボックスの変更ハンドラー
  const handleZaikoNashiChange = (e) => {
    const checked = e.target.checked;
    setZaikoNashi(checked);
  };

  // 部品追加ボタンのクリックハンドラー
  const handleAddPartsClick = async () => {
    if (!onAddParts) {
      console.error("onAddParts callback is not provided");
      return;
    }

    try {
      const result = await onAddParts(quote_number, data?.answer?.[0]?.RequestAnswer?.id);
      if (result && result.paid && result.seqnum) {
        const newIndex = rowCount;
        const newItem = {
          PAID: result.paid,
          seqnum: result.seqnum || newIndex + 2,
          Bunrui: "",
          OthPartsSonota: "",
          PartsJyunseiCode: "",
          Suryo: 1,
          PartsKindID: "",
          Price: "",
          DeliveryPrice: "",
          Hasso: "",
          Sell_Limit: "",
          ColorNo: "",
          ColorName: "",
          biko: "",
          JishaNo: "",
          PrepKind: "",
          Hacchu_flg: "",
        };
        setAddList([...addList, newItem]);
        setRowCount(newIndex + 1);
      }
    } catch (error) {
      console.error("Failed to add parts:", error);
    }
  };

  // 部品削除ボタンのクリックハンドラー
  const handleDeletePartsClick = async (index, paid, seqnum) => {
    if (!onDeleteParts) {
      console.error("onDeleteParts callback is not provided");
      return;
    }

    try {
      await onDeleteParts(quote_number, 0, data?.answer?.[0]?.RequestAnswer?.id, seqnum);
      const newList = addList.filter((_, i) => i !== index);
      setAddList(newList);
    } catch (error) {
      console.error("Failed to delete parts:", error);
    }
  };

  // 追加商品の入力フィールド変更ハンドラー
  const handleAddFieldChange = (index, field, value) => {
    const newList = [...addList];
    if (!newList[index]) {
      newList[index] = {};
    }
    newList[index][field] = value;
    setAddList(newList);
  };

  // 自社品/他社品ラジオボタンのトグル処理
  const handlePrepKindToggle = (index, value) => {
    const currentValue = addList[index]?.PrepKind;
    if (currentValue === value) {
      // 同じ値を選択した場合は解除
      handleAddFieldChange(index, "PrepKind", "");
    } else {
      handleAddFieldChange(index, "PrepKind", value);
    }
  };

  // 部品備考への追加ボタン処理
  const handleAddBikou = (index) => {
    const hoshoSelect = data?.hoshoSelect || [];
    const selectedValue = document.getElementById(`hselect_${index}`)?.value || "";
    const currentBiko = addList[index]?.biko || "";
    handleAddFieldChange(index, "biko", currentBiko + selectedValue);
  };

  if (!data) {
    return null;
  }

  return (
    <>
      {/* ヘッダーセクション */}
      <s.PartsTitle>
        部品情報3
        {/* 質問ボタン (1番目の部品の部品コードを→　部品ごとに分けるようなら空に) */}
        <s.QuestionButton
          style={{ verticalAlign: "middle", marginTop: "-5px" }}
          data-key={partsList.length > 0 ? partsList[0]?.PartsRequest?.id || "" : ""}
          onClick={() => {
            if (onOpenQaDialog && partsList.length > 0 && partsList[0]?.PartsRequest?.id) {
              onOpenQaDialog(quote_number, partsList[0].PartsRequest.id);
            }
          }}
        >
          依頼元へ質問を行う | 回答を見る
        </s.QuestionButton>
        {isGensha && (
          <s.StockedVehicleButton
            style={{ verticalAlign: "middle", marginTop: "-5px" }}
            className="important-btn4"
            onClick={() => {
              if (onOpenGenshaDetail && data?.answer?.[0]?.RequestAnswer?.GenshaID) {
                onOpenGenshaDetail(data.answer[0].RequestAnswer.GenshaID);
              }
            }}
          >
            <Image src="/images/gensha_icon_w.png" alt="入庫済み車輌" width={20} height={20} />
            入庫済みの車輌があります
          </s.StockedVehicleButton>
        )}
      </s.PartsTitle>

      {isAfterCarryTaxinDate && <strong style={{ color: "#0000ff", fontSize: "12pt" }}>　※送料は「税別」にて記載してください。</strong>}

      {data?.answer?.[0]?.CA?.UserName && (
        <div style={{ marginTop: "10px" }}>
          <span style={{ backgroundColor: "#ff3333", fontSize: "16px", color: "#ffffff" }}>　最終回答者： {data.answer[0].CA.UserName} 様&nbsp;&nbsp;&nbsp;</span>
        </div>
      )}

      {/* 部品取り一括見積セクション */}
      <s.PartsConfirmTable>
        <tbody>
          <tr>
            <s.PartsNameHeaderGensha colSpan={6} scope="row">
              部品取り一括見積
              <span style={{ float: "right" }}>
                {jyotai}
                {jyotai && <br />}
                {btNameQab && (
                  <img
                    src={`/images/${btNameQab}.png`}
                    className="qabOpen btn_gray"
                    data-key={`${data?.answer?.[0]?.RequestAnswer?.id}/${sessionData == null || sessionData === undefined ? firstParts?.PartsAnswer?.id : sessionData?.Answer?.[0]?.[0]?.PAID || firstParts?.PartsAnswer?.id}`}
                    style={{ cursor: "pointer" }}
                    alt="在庫確認"
                    onClick={() => {
                      if (onOpenQabDialog) {
                        // PHPテンプレート: ($SessionData == null)? $partsList[0]['PartsAnswer']['id'] : $SessionData['Answer'][0][0]['PAID']
                        // PHPテンプレートではPAIDが空の場合のフォールバックはないが、data-key属性では使用される
                        const paid = sessionData == null || sessionData === undefined ? firstParts?.PartsAnswer?.id : sessionData?.Answer?.[0]?.[0]?.PAID || firstParts?.PartsAnswer?.id;
                        onOpenQabDialog(quote_number, `${data?.answer?.[0]?.RequestAnswer?.id}/${paid}`);
                      }
                    }}
                  />
                )}
              </span>
              <p>(依頼者備考：{data?.request?.OrderedRequest?.Bikou || ""})</p>
            </s.PartsNameHeaderGensha>
          </tr>
          <tr>
            <s.ConfirmTableHeader>
              一括見積備考
              <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" style={{ display: zaikoNashi ? "inline" : "none" }} id="zaiko_must" />
            </s.ConfirmTableHeader>
            <s.ConfirmTableHeader>価格(税別)</s.ConfirmTableHeader>
            <s.ConfirmTableHeader>送料(税別)</s.ConfirmTableHeader>
            <s.ConfirmTableHeader>発送可能日</s.ConfirmTableHeader>
            <s.ConfirmTableHeader>見積有効期限</s.ConfirmTableHeader>
            <s.ConfirmTableHeader>写真</s.ConfirmTableHeader>
          </tr>
          <tbody id="parts0">
            {viewFlg === 0 ? (
              // 入力モード
              <tr>
                <s.ConfirmTableCell style={{ textAlign: "left" }}>
                  {!firstParts && (
                    <label>
                      <input type="checkbox" name="data[Answer][0][0][ZaikoNashi]" id="zaikonashi_0_0" value="1" checked={zaikoNashi} onChange={handleZaikoNashiChange} />
                      在庫なしで回答を行う
                    </label>
                  )}
                  <textarea name="data[Answer][0][0][biko]" id="biko_0_0" style={{ height: "5em", width: "98%", minWidth: "500px" }} value={biko} onChange={(e) => setBiko(e.target.value)} />
                </s.ConfirmTableCell>
                <s.ConfirmTableCell>
                  {!firstParts ? (
                    <>
                      <input type="text" name="data[Answer][0][0][Price]" id="price_0_0" value={price} onChange={(e) => setPrice(e.target.value)} size="35" maxLength={10} disabled={zaikoNashi} />円
                    </>
                  ) : (
                    <>{zaikoNashi ? "在庫なし" : <>{formatNumber(firstParts?.PartsAnswer?.StanderdPrice)}円</>}</>
                  )}
                </s.ConfirmTableCell>
                <s.ConfirmTableCell>
                  {!firstParts ? (
                    <>
                      <input
                        type="text"
                        name="data[Answer][0][0][DeliveryPrice]"
                        id="delprice_0_0"
                        value={deliveryPrice}
                        onChange={(e) => setDeliveryPrice(e.target.value)}
                        size="35"
                        maxLength={10}
                        disabled={zaikoNashi}
                      />
                      円
                    </>
                  ) : (
                    <>{!zaikoNashi && <>{formatNumber(firstParts?.PartsAnswer?.Soryo)}円</>}</>
                  )}
                </s.ConfirmTableCell>
                <s.ConfirmTableCell>
                  {(!firstParts || !zaikoNashi) && (
                    <select name="data[Answer][0][0][Hasso]" id="hasso_status_0_0" value={hasso} onChange={(e) => setHasso(e.target.value)} disabled={zaikoNashi}>
                      <option value=""></option>
                      {Consts.SELECTION.HASSO_STATUS.filter((s) => [1, 2, 3, 5, 10].includes(s.key)).map((status) => (
                        <option key={status.key} value={status.key}>
                          {status.value}
                        </option>
                      ))}
                    </select>
                  )}
                </s.ConfirmTableCell>
                <s.ConfirmTableCell>
                  {(!firstParts || !zaikoNashi) && (
                    <input
                      type="text"
                      name="data[Answer][0][0][Sell_Limit]"
                      id="requestlimit_0_0"
                      value={sellLimit}
                      onChange={(e) => setSellLimit(e.target.value)}
                      size="35"
                      maxLength={10}
                      disabled={zaikoNashi}
                    />
                  )}
                </s.ConfirmTableCell>
                <s.ConfirmTableCell>
                  <s.FileIcon
                    src="/images/camera_icon.png"
                    alt="写真"
                    onClick={() => {
                      if (photoDialogProps?.open) {
                        photoDialogProps.open({
                          mitsumoriNo: quote_number,
                          prid: 0,
                          seqnum: 1,
                          refMode: Consts.WRECKER_PHOTO.KARI_WRT,
                        });
                      }
                    }}
                  />
                </s.ConfirmTableCell>
              </tr>
            ) : (
              // ラベル表示モード
              <tr className={bgcolor || undefined}>
                <s.ConfirmTableCell style={{ whiteSpace: "pre-line", textAlign: "left" }}>
                  {firstParts?.PartsAnswer?.Bikou
                    ? firstParts.PartsAnswer.Bikou.split("\n").map((line, idx, arr) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))
                    : ""}
                </s.ConfirmTableCell>
                <s.ConfirmTableCell>{zaikoNashi ? "在庫なし" : <>{formatNumber(firstParts?.PartsAnswer?.StanderdPrice)}円</>}</s.ConfirmTableCell>
                <s.ConfirmTableCell>{!zaikoNashi && <>{formatNumber(firstParts?.PartsAnswer?.Soryo)}円</>}</s.ConfirmTableCell>
                <s.ConfirmTableCell>{getHassoStatusText(firstParts?.PartsAnswer?.Hasso)}</s.ConfirmTableCell>
                <s.ConfirmTableCell>{!zaikoNashi && formatDate(firstParts?.PartsAnswer?.Sell_Limit)}</s.ConfirmTableCell>
                <s.ConfirmTableCell>
                  <s.FileIcon
                    src="/images/camera_icon.png"
                    alt="写真"
                    onClick={() => {
                      if (photoDialogProps?.open) {
                        photoDialogProps.open({
                          mitsumoriNo: quote_number,
                          prid: 0,
                          seqnum: 1,
                          refMode: Consts.WRECKER_PHOTO.KARI_REF,
                        });
                      }
                    }}
                  />
                </s.ConfirmTableCell>
              </tr>
            )}
          </tbody>
        </tbody>
      </s.PartsConfirmTable>

      <div>
        <font color="#0000ff">
          販売済み、解体済み等の場合は、備考へその旨を記載してください。
          <br />
          生産済みの代替品等をご提案の場合は併せて＜追加商品情報入力＞をご利用ください。
        </font>
      </div>

      {/* 追加商品情報入力セクション */}
      <s.PartsTitle>
        追加商品情報入力
        {isAfterCarryTaxinDate && <strong style={{ color: "#0000ff", fontSize: "12pt" }}>　※送料は「税別」にて記載してください。</strong>}
      </s.PartsTitle>

      <s.PartsConfirmTable id="addparts">
        <tbody id="item">
          {addList.map((addItem, j) => {
            // PHPテンプレート: $paid = ($SessionData == null)? '' : $SessionData['Add'][$j]['PAID'];
            // PHPの$SessionData == nullはnullまたは未定義の場合にtrueになる
            const isSessionDataNull = sessionData == null || sessionData === undefined;
            const paid = isSessionDataNull ? "" : addItem?.PAID || "";
            const btNameQab = getQabButtonName(paid);
            // PHPテンプレート: $hacchu_flg = empty($SessionData['Add'][$j]['Hacchu_flg']) ? '' : $SessionData['Add'][$j]['Hacchu_flg'];
            const hacchuFlg = isSessionDataNull || !addItem?.Hacchu_flg ? "" : addItem.Hacchu_flg;
            const status = getHacchuStatus(hacchuFlg);
            const addJyotai = status.jyotai;
            const addBgcolor = status.bgcolor;
            // PHPテンプレート: $view_flg = 1 (ラベル表示) から開始し、$hacchu_flg が空の場合は 0 (入力) にする
            // empty($SessionData['Add'])の場合は$view_flg = 0になるが、これはaddListが空の場合に既に処理されている
            const addViewFlg = addJyotai ? 1 : 0; // $hacchu_flgが空の場合、$jyotaiが空になるので0（入力）になる

            if (addViewFlg === 0) {
              // 入力モード
              return (
                <React.Fragment key={j}>
                  <tr>
                    <s.PartsNameHeaderGensha id={`apname${j}`} colSpan={14} scope="row">
                      分類：
                      <select name={`data[Add][${j}][Bunrui]`} id={`addbunrui_${j}`} value={addItem?.Bunrui || ""} onChange={(e) => handleAddFieldChange(j, "Bunrui", e.target.value)}>
                        <option value="">▼選択してください</option>
                        {Object.entries(data?.sbunrui || {}).map(([key, val]) => (
                          <option key={key} value={key}>
                            {val}
                          </option>
                        ))}
                      </select>
                      　品名：
                      <input
                        type="text"
                        name={`data[Add][${j}][OthPartsSonota]`}
                        id={`addpsonota_${j}`}
                        value={addItem?.OthPartsSonota || ""}
                        size="50"
                        maxLength={200}
                        style={{ width: "20em" }}
                        onChange={(e) => handleAddFieldChange(j, "OthPartsSonota", e.target.value)}
                      />
                    </s.PartsNameHeaderGensha>
                  </tr>
                  <tr>
                    <s.ConfirmTableHeader>&nbsp;</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader scope="row">純正品番</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>数量</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      部品カテゴリ
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      価格(税別)
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      送料(税別)
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      発送可能日
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      見積有効期限
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>カラー番号</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>カラー名称</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>写真</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>部品備考</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader id={`akomoku${j}`} scope="row">
                      自社管理番号
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>&nbsp;</s.ConfirmTableHeader>
                  </tr>
                  <tr>
                    <s.ConfirmTableCell>
                      {btNameQab && (
                        <img
                          src={`/images/${btNameQab}.png`}
                          className="qabOpen btn_gray"
                          data-key={`${data?.answer?.[0]?.RequestAnswer?.id}/${paid}`}
                          style={{ cursor: "pointer" }}
                          alt="在庫確認"
                          onClick={() => {
                            if (onOpenQabDialog) {
                              onOpenQabDialog(quote_number, `${data?.answer?.[0]?.RequestAnswer?.id}/${paid}`);
                            }
                          }}
                        />
                      )}
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['PartsJyunseiCode'])) ? '' : $SessionData['Add'][$j]['PartsJyunseiCode'] */}
                      <input
                        type="text"
                        name={`data[Add][${j}][PartsJyunseiCode]`}
                        id={`addjyunseicd_${j}`}
                        value={isSessionDataNull || !addItem?.PartsJyunseiCode ? "" : addItem.PartsJyunseiCode}
                        size="35"
                        maxLength={20}
                        style={{ width: "10em" }}
                        onChange={(e) => handleAddFieldChange(j, "PartsJyunseiCode", e.target.value)}
                      />
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>1</s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? 0 : (empty($SessionData['Add'][$j]['PartsKindID'])) ? 0 : $SessionData['Add'][$j]['PartsKindID'] */}
                      <select
                        name={`data[Add][${j}][PartsKindID]`}
                        id={`addpartskind_${j}`}
                        value={isSessionDataNull || !addItem?.PartsKindID ? "" : addItem.PartsKindID}
                        onChange={(e) => handleAddFieldChange(j, "PartsKindID", e.target.value)}
                      >
                        <option value=""></option>
                        {(data?.partskind || []).map((category) => (
                          <option key={category?.Partskind?.PartsKindID} value={category?.Partskind?.PartsKindID}>
                            {category?.Partskind?.PartsKindName}
                          </option>
                        ))}
                      </select>
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: if($SessionData == null or $hacchu_flg == '') */}
                      {isSessionDataNull || !hacchuFlg ? (
                        <>
                          {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['Price'])) ? '' : $SessionData['Add'][$j]['Price'] */}
                          <input
                            type="text"
                            name={`data[Add][${j}][Price]`}
                            id={`addprice_${j}`}
                            value={isSessionDataNull || !addItem?.Price ? "" : addItem.Price}
                            size="35"
                            maxLength={10}
                            onChange={(e) => handleAddFieldChange(j, "Price", e.target.value)}
                          />
                          円
                        </>
                      ) : (
                        <>{formatNumber(addItem?.Price)}円</>
                      )}
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: if($SessionData == null or $hacchu_flg == '') */}
                      {isSessionDataNull || !hacchuFlg ? (
                        <>
                          {/* PHPテンプレート: ($SessionData == null) ? '' : (!isset($SessionData['Add'][$j]['DeliveryPrice'])) ? '' : $SessionData['Add'][$j]['DeliveryPrice'] */}
                          <input
                            type="text"
                            name={`data[Add][${j}][DeliveryPrice]`}
                            id={`adddelprice_${j}`}
                            value={isSessionDataNull || addItem?.DeliveryPrice == null ? "" : addItem.DeliveryPrice}
                            size="35"
                            maxLength={10}
                            onChange={(e) => handleAddFieldChange(j, "DeliveryPrice", e.target.value)}
                          />
                          円
                        </>
                      ) : (
                        <>{formatNumber(addItem?.DeliveryPrice)}円</>
                      )}
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? 0 : (empty($SessionData['Add'][$j]['Hasso'])) ? 0 : $SessionData['Add'][$j]['Hasso'] */}
                      <select
                        name={`data[Add][${j}][Hasso]`}
                        id={`addhasso_status_${j}`}
                        value={isSessionDataNull || !addItem?.Hasso ? "" : addItem.Hasso}
                        onChange={(e) => handleAddFieldChange(j, "Hasso", e.target.value)}
                      >
                        <option value=""></option>
                        {Consts.SELECTION.HASSO_STATUS.filter((s) => [1, 2, 3, 5].includes(s.key)).map((status) => (
                          <option key={status.key} value={status.key}>
                            {status.value}
                          </option>
                        ))}
                      </select>
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['Sell_Limit'])) ? '' : $SessionData['Add'][$j]['Sell_Limit'] */}
                      <input
                        name={`data[Add][${j}][Sell_Limit]`}
                        type="text"
                        size="10"
                        id={`addrequestlimit_${j}`}
                        value={isSessionDataNull || !addItem?.Sell_Limit ? "" : addItem.Sell_Limit}
                        maxLength={10}
                        onChange={(e) => handleAddFieldChange(j, "Sell_Limit", e.target.value)}
                      />
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['ColorNo'])) ? '' : $SessionData['Add'][$j]['ColorNo'] */}
                      <input
                        type="text"
                        name={`data[Add][${j}][ColorNo]`}
                        id={`addcolorno_${j}`}
                        value={isSessionDataNull || !addItem?.ColorNo ? "" : addItem.ColorNo}
                        size="35"
                        maxLength={20}
                        onChange={(e) => handleAddFieldChange(j, "ColorNo", e.target.value)}
                      />
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['ColorName'])) ? '' : $SessionData['Add'][$j]['ColorName'] */}
                      <input
                        type="text"
                        name={`data[Add][${j}][ColorName]`}
                        id={`addcolorname_${j}`}
                        value={isSessionDataNull || !addItem?.ColorName ? "" : addItem.ColorName}
                        size="35"
                        maxLength={20}
                        onChange={(e) => handleAddFieldChange(j, "ColorName", e.target.value)}
                      />
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      <s.FileIcon
                        src="/images/camera_icon.png"
                        alt="写真"
                        onClick={() => {
                          if (photoDialogProps?.open) {
                            photoDialogProps.open({
                              mitsumoriNo: quote_number,
                              prid: 0,
                              seqnum: isSessionDataNull || !addItem?.seqnum ? j + 2 : addItem.seqnum,
                              refMode: Consts.WRECKER_PHOTO.KARI_WRT,
                              othPName: "追加商品",
                            });
                          }
                        }}
                      />
                      <br />
                      <s.FileIcon
                        src="/images/file_icon.png"
                        alt="ファイル"
                        style={{ cursor: "pointer", padding: "5px 0px 0px" }}
                        onClick={() => {
                          if (fileupDialogProps?.open) {
                            fileupDialogProps.open({
                              mitsumoriNo: quote_number,
                              prid: 0,
                              seqnum: isSessionDataNull || !addItem?.seqnum ? j + 2 : addItem.seqnum,
                              refMode: Consts.WRECKER_PHOTO.KARI_WRT,
                              isEditMode: true,
                            });
                          }
                        }}
                      />
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      {data?.hoshoSelect && data.hoshoSelect.length > 0 && (
                        <>
                          <select id={`hselect_${j}`}>
                            {data.hoshoSelect.map((hs, idx) => (
                              <option key={idx} value={hs}>
                                {hs}
                              </option>
                            ))}
                          </select>
                          <input type="button" className="AddBikou" slt={j} value="追加" onClick={() => handleAddBikou(j)} />
                          <br />
                        </>
                      )}
                      {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['biko'])) ? '' : $SessionData['Add'][$j]['biko'] */}
                      <textarea
                        name={`data[Add][${j}][biko]`}
                        id={`addbiko_${j}`}
                        value={isSessionDataNull || !addItem?.biko ? "" : addItem.biko}
                        size="35"
                        maxLength={100}
                        style={{ imeMode: "inactive" }}
                        onChange={(e) => handleAddFieldChange(j, "biko", e.target.value)}
                      />
                    </s.ConfirmTableCell>
                    <s.CompanyCodeCellBg>
                      {/* PHPテンプレート: ($SessionData == null) ? '' : (empty($SessionData['Add'][$j]['JishaNo'])) ? '' : $SessionData['Add'][$j]['JishaNo'] */}
                      <input
                        type="text"
                        name={`data[Add][${j}][JishaNo]`}
                        id={`addjishano_${j}`}
                        value={isSessionDataNull || !addItem?.JishaNo ? "" : addItem.JishaNo}
                        size="35"
                        maxLength={20}
                        style={{ imeMode: "inactive" }}
                        onChange={(e) => handleAddFieldChange(j, "JishaNo", e.target.value)}
                      />
                      <br />
                      {/* PHPテンプレート: ($SessionData == null)? 0 : (empty($SessionData['Add'][$j]['PrepKind']))? 0 : $SessionData['Add'][$j]['PrepKind'] */}
                      {/* PHPでは empty(0) は true なので、PrepKind が 0 の場合は未選択扱い */}
                      <label>
                        <input
                          type="radio"
                          name={`data[Add][${j}][PrepKind]`}
                          id={`addprepkind_${j}_inhouse`}
                          className={`addprepkind_${j}`}
                          value={Consts.GOODS.INHOUSE}
                          checked={!isSessionDataNull && addItem?.PrepKind && addItem.PrepKind === Consts.GOODS.INHOUSE}
                          onChange={() => handlePrepKindToggle(j, Consts.GOODS.INHOUSE)}
                        />
                        自社品
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`data[Add][${j}][PrepKind]`}
                          id={`addprepkind_${j}_other`}
                          className={`addprepkind_${j}`}
                          value={Consts.GOODS.OTHER}
                          checked={!isSessionDataNull && addItem?.PrepKind && addItem.PrepKind === Consts.GOODS.OTHER}
                          onChange={() => handlePrepKindToggle(j, Consts.GOODS.OTHER)}
                        />
                        他社品
                      </label>
                    </s.CompanyCodeCellBg>
                    <s.ConfirmTableCell>
                      {/* PHPテンプレート: ($SessionData == null) ? 2 : (empty($SessionData['Add'][$j]['seqnum'])) ? 2 : $SessionData['Add'][$j]['seqnum'] */}
                      <a
                        id={`arow_cancel_${j}`}
                        style={{ cursor: "pointer" }}
                        className="btn_black row_cancel"
                        onClick={() => handleDeletePartsClick(j, paid, isSessionDataNull || !addItem?.seqnum ? j + 2 : addItem.seqnum)}
                      >
                        取消
                      </a>
                    </s.ConfirmTableCell>
                  </tr>
                </React.Fragment>
              );
            } else {
              // ラベル表示モード
              return (
                <React.Fragment key={j}>
                  <tr>
                    <s.PartsNameHeaderGensha id={`apname${j}`} colSpan={13} scope="row">
                      {addItem?.OthPartsName}　品名：{addItem?.OthPartsSonota}
                      <span style={{ float: "right" }}>{addJyotai}</span>
                    </s.PartsNameHeaderGensha>
                  </tr>
                  <tr>
                    <s.ConfirmTableHeader scope="row">純正品番</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>数量</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      部品カテゴリ
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      価格(税別)
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      送料(税別)
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      発送可能日
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>
                      見積有効期限
                      <br />
                      <Image src="/images/must.png" alt="必須" width={16} height={16} className="must" />
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>カラー番号</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>カラー名称</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>写真</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>部品備考</s.ConfirmTableHeader>
                    <s.ConfirmTableHeader id={`akomoku${j}`} scope="row">
                      自社管理番号
                    </s.ConfirmTableHeader>
                    <s.ConfirmTableHeader>&nbsp;</s.ConfirmTableHeader>
                  </tr>
                  <tr className={addBgcolor || undefined}>
                    <s.ConfirmTableCell>{addItem?.PartsJyunseiCode}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>1</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{getCategoryName(addItem?.PartsKindID, data?.partskind)}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formatNumber(addItem?.Price)}円</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{addItem?.DeliveryPrice === 0 ? "送料込" : `${formatNumber(addItem?.DeliveryPrice)}円`}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{getHassoStatusText(addItem?.Hasso)}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{formatDate(addItem?.Sell_Limit)}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{addItem?.ColorNo}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>{addItem?.ColorName}</s.ConfirmTableCell>
                    <s.ConfirmTableCell>
                      <s.FileIcon
                        src="/images/camera_icon.png"
                        alt="写真"
                        onClick={() => {
                          if (photoDialogProps?.open) {
                            photoDialogProps.open({
                              mitsumoriNo: quote_number,
                              prid: 0,
                              seqnum: isSessionDataNull || !addItem?.seqnum ? j + 2 : addItem.seqnum,
                              refMode: Consts.WRECKER_PHOTO.KARI_REF,
                              othPName: "追加商品",
                            });
                          }
                        }}
                      />
                      <br />
                      <s.FileIcon
                        src="/images/file_icon.png"
                        alt="ファイル"
                        style={{ cursor: "pointer", padding: "5px 0px 0px" }}
                        onClick={() => {
                          if (fileupDialogProps?.open) {
                            fileupDialogProps.open({
                              mitsumoriNo: quote_number,
                              prid: 0,
                              seqnum: isSessionDataNull || !addItem?.seqnum ? j + 2 : addItem.seqnum,
                              refMode: Consts.WRECKER_PHOTO.KARI_REF,
                              isEditMode: false,
                            });
                          }
                        }}
                      />
                    </s.ConfirmTableCell>
                    <s.ConfirmTableCell style={{ whiteSpace: "pre-line", textAlign: "left" }}>
                      {addItem?.biko
                        ? addItem.biko.split("\n").map((line, idx, arr) => (
                            <React.Fragment key={idx}>
                              {line}
                              {idx < arr.length - 1 && <br />}
                            </React.Fragment>
                          ))
                        : ""}
                    </s.ConfirmTableCell>
                    <s.CompanyCodeCellBg>
                      {addItem?.JishaNo}
                      <br />
                      {addItem?.PrepKind === Consts.GOODS.INHOUSE && <label>自社品</label>}
                      {addItem?.PrepKind === Consts.GOODS.OTHER && <label>他社品</label>}
                    </s.CompanyCodeCellBg>
                    <s.ConfirmTableCell>&nbsp;</s.ConfirmTableCell>
                  </tr>
                </React.Fragment>
              );
            }
          })}
        </tbody>
        <tr>
          <s.ConfirmTableCell colSpan={14} style={{ textAlign: "right" }} scope="row">
            <a 
              // href="javascript:void(0)" 
              className="btn_gray appendlow"
              onClick={handleAddPartsClick} 
              style={{ cursor: "pointer" }}
            >
              部品の追加をする
            </a>
          </s.ConfirmTableCell>
        </tr>
      </s.PartsConfirmTable>
    </>
  );
}
